import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CONTACT_INFO } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name, email, phone: phone || null, subject: subject || null, message,
      });
      setSuccess(true);
      toast.success("Message envoyé. Léa vous répond très vite !");
    } catch (err) {
      console.error(err);
      toast.error("Une erreur est survenue. Réessayez ou écrivez directement à Léa.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div data-testid="contact-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-20 md:py-28">
      <p className="text-xs tracking-[0.25em] uppercase text-[#8B7E74] mb-6 fade-up">Contact</p>
      <h1 data-testid="contact-title" className="font-serif text-5xl md:text-6xl lg:text-7xl text-[#4A403A] leading-[1.05] mb-6 max-w-3xl fade-up fade-up-delay-1">
        Écrivez-moi,<br/>
        <em className="text-[#BA8E82]">je vous lirai</em>.
      </h1>
      <p className="text-lg text-[#8B7E74] max-w-2xl leading-relaxed mb-16 fade-up fade-up-delay-2">
        Une question, un doute, un simple bonjour — toutes les demandes sont les bienvenues.
        Je vous réponds généralement sous 24h ouvrées.
      </p>

      <div className="grid lg:grid-cols-12 gap-10">
        {/* Coordonnées */}
        <aside className="lg:col-span-5 space-y-5">
          <div className="bg-white rounded-3xl p-8 border border-[#E5DCD6] shadow-softer">
            <p className="text-xs tracking-[0.2em] uppercase text-[#8B7E74] mb-6">Coordonnées</p>
            <div className="space-y-5">
              <a href={`tel:${CONTACT_INFO.phoneRaw}`} data-testid="contact-phone-link" className="flex items-start gap-4 group">
                <span className="w-11 h-11 rounded-full bg-[#F4EBE6] flex items-center justify-center text-[#BA8E82] shrink-0">
                  <Phone size={16} />
                </span>
                <div>
                  <p className="text-xs text-[#8B7E74] uppercase tracking-wide">Téléphone</p>
                  <p className="text-[#4A403A] text-lg group-hover:text-[#BA8E82] transition-colors">{CONTACT_INFO.phone}</p>
                </div>
              </a>
              <a href={`mailto:${CONTACT_INFO.email}`} data-testid="contact-email-link" className="flex items-start gap-4 group">
                <span className="w-11 h-11 rounded-full bg-[#F4EBE6] flex items-center justify-center text-[#BA8E82] shrink-0">
                  <Mail size={16} />
                </span>
                <div>
                  <p className="text-xs text-[#8B7E74] uppercase tracking-wide">Email</p>
                  <p className="text-[#4A403A] text-lg group-hover:text-[#BA8E82] transition-colors break-all">{CONTACT_INFO.email}</p>
                </div>
              </a>
              <div className="flex items-start gap-4">
                <span className="w-11 h-11 rounded-full bg-[#F4EBE6] flex items-center justify-center text-[#BA8E82] shrink-0">
                  <MapPin size={16} />
                </span>
                <div>
                  <p className="text-xs text-[#8B7E74] uppercase tracking-wide">Zone d'intervention</p>
                  <p className="text-[#4A403A] text-lg">{CONTACT_INFO.zone}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="w-11 h-11 rounded-full bg-[#F4EBE6] flex items-center justify-center text-[#BA8E82] shrink-0">
                  <Clock size={16} />
                </span>
                <div>
                  <p className="text-xs text-[#8B7E74] uppercase tracking-wide">Disponibilité</p>
                  <p className="text-[#4A403A] text-lg">{CONTACT_INFO.hours}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#4A403A] rounded-3xl p-8 text-[#FAF7F2]">
            <p className="text-xs tracking-[0.2em] uppercase text-[#D2A69A] mb-4">Note importante</p>
            <p className="text-[#FAF7F2]/90 leading-relaxed text-sm">
              En cas d'urgence concernant la santé de votre enfant, contactez votre médecin,
              le 15 (SAMU) ou rendez-vous aux urgences pédiatriques.
            </p>
          </div>
        </aside>

        {/* Form */}
        <div className="lg:col-span-7">
          {success ? (
            <div data-testid="contact-success" className="bg-white rounded-3xl p-10 md:p-14 border border-[#E5DCD6] shadow-softer text-center">
              <span className="inline-flex w-16 h-16 rounded-full bg-[#F4EBE6] items-center justify-center text-[#BA8E82] mb-6">
                <CheckCircle2 size={28} strokeWidth={1.4} />
              </span>
              <h3 className="font-serif text-4xl text-[#4A403A] mb-4">Message bien reçu&nbsp;!</h3>
              <p className="text-[#8B7E74] leading-relaxed max-w-md mx-auto">
                Merci de votre confiance. Léa vous répondra dès que possible, généralement sous 24h ouvrées.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-8 md:p-10 border border-[#E5DCD6] shadow-softer space-y-5">
              <h2 className="font-serif text-3xl text-[#4A403A] mb-2">Votre message</h2>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="c-name" className="text-xs tracking-[0.15em] uppercase text-[#8B7E74]">Nom complet</Label>
                  <Input id="c-name" required value={name} onChange={(e) => setName(e.target.value)} data-testid="contact-name" className="mt-2 h-12 rounded-xl border-[#E5DCD6] bg-white/60" />
                </div>
                <div>
                  <Label htmlFor="c-email" className="text-xs tracking-[0.15em] uppercase text-[#8B7E74]">Email</Label>
                  <Input id="c-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="contact-email" className="mt-2 h-12 rounded-xl border-[#E5DCD6] bg-white/60" />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="c-phone" className="text-xs tracking-[0.15em] uppercase text-[#8B7E74]">Téléphone (optionnel)</Label>
                  <Input id="c-phone" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="contact-phone" className="mt-2 h-12 rounded-xl border-[#E5DCD6] bg-white/60" />
                </div>
                <div>
                  <Label htmlFor="c-subject" className="text-xs tracking-[0.15em] uppercase text-[#8B7E74]">Sujet</Label>
                  <Input id="c-subject" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="contact-subject" className="mt-2 h-12 rounded-xl border-[#E5DCD6] bg-white/60" />
                </div>
              </div>

              <div>
                <Label htmlFor="c-message" className="text-xs tracking-[0.15em] uppercase text-[#8B7E74]">Message</Label>
                <Textarea id="c-message" required rows={6} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="contact-message" className="mt-2 rounded-xl border-[#E5DCD6] bg-white/60" />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 bg-[#4A403A] text-[#FAF7F2] text-sm tracking-wide transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#3a322d] disabled:opacity-60"
              >
                {submitting ? "Envoi en cours…" : (<>Envoyer mon message <Send size={15} /></>)}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
